import {
  apiConversations,
  CONVERSAS_LIMITE_CARGA,
} from '@modules/aawz-intelligence-v2/api/apiConversations'
import { apiMessages } from '@modules/aawz-intelligence-v2/api/apiMessages'
import { HTTP_OK } from '@modules/aawz-intelligence-v2/constants/Http'
import { ERRO_AO_ABRIR_CONVERSA } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { mensagemAmigavelDoErro } from '@modules/aawz-intelligence-v2/utils/mensagem-de-erro'

const STORE = 'aawz-intelligence-v2'

/**
 * Gestão do histórico de conversas nas superfícies COMPACTAS do chat — o popup
 * global e o painel lateral do Hub.
 *
 * As duas mostram a mesma lista (`Conversations`) fora do motor, porque em modo
 * `widget` a sidebar dele não cabe. Isso obrigava cada uma a reimplementar
 * abrir / criar / renomear / apagar, e elas o fizeram com os mesmos corpos e
 * nomes traduzidos (`setNewMessage` × `novaConversa`, `reloadConversations` ×
 * `recarregarConversas`). Três métodos eram idênticos linha a linha.
 *
 * A rota cheia NÃO usa isto: lá quem cuida do histórico é o próprio motor, com
 * a sidebar dentro dele.
 *
 * ## Contrato do consumidor
 *
 * - `data.mostrandoHistorico` (Boolean) — o mixin abre e fecha por conta.
 * - opcional `adotarAgenteDaConversa(conversa)` — chamado ANTES de carregar as
 *   mensagens. Quem tem agente variável (o popup) troca o agente ali; quem tem
 *   agente fixo (o painel do Consultor) simplesmente não implementa.
 */
export const historicoConversas = {
  data() {
    return {
      mostrandoHistorico: false,
    }
  },
  methods: {
    alternarHistorico() {
      this.mostrandoHistorico = !this.mostrandoHistorico
      if (this.mostrandoHistorico) this.recarregarConversas()
    },
    // Conversa nova: limpa a seleção e as mensagens do store compartilhado.
    novaConversa() {
      this.$store.dispatch(STORE + '/setSelectedConversation', null)
      this.$store.dispatch(STORE + '/setMessages', [])
      this.mostrandoHistorico = false
    },
    async recarregarConversas() {
      // Sem persona: o histórico lista todos os agentes. O gestor não lembra de
      // qual agente era a conversa que procura — filtrar obrigava a adivinhar
      // antes de achar. Quem popula a lista no fluxo normal é o
      // `carregarConversas` do motor, que filtra pela persona ativa; recarregar
      // ao ABRIR o histórico é o que faz as duas coisas conviverem.
      await apiConversations.carregar({
        limit: CONVERSAS_LIMITE_CARGA,
        offset: 0,
        todosAgentes: true,
      })
    },
    async abrirConversa(conversa) {
      if (!conversa || !conversa.id) return
      // Geração da carga (mesmo padrão de `cargaDeConversas` em view-chat.vue):
      // dois cliques rápidos em conversas diferentes deixavam a resposta mais
      // LENTA gravar as mensagens da conversa errada. Não vai em `data` — é só
      // um contador de instância, sem UI dependendo dele.
      const carga = (this._cargaDeMensagens || 0) + 1
      this._cargaDeMensagens = carga
      // Antes de carregar as mensagens: quem troca de agente aqui precisa que a
      // troca já tenha assentado, senão o watcher do motor chama `novaConversa`
      // e limpa exatamente a conversa que acabou de abrir.
      if (this.adotarAgenteDaConversa) {
        await this.adotarAgenteDaConversa(conversa)
        if (carga !== this._cargaDeMensagens) return
      }
      this.selecionarConversa(conversa)
      const r = await apiMessages.getMessages({ conversationId: conversa.id })
      // Stale: outra conversa foi aberta enquanto esta carregava — ela é quem
      // grava e solta o loading.
      if (carga !== this._cargaDeMensagens) return
      this.receberMensagensDaConversa(r)
      this.mostrandoHistorico = false
    },
    // Seleciona e limpa o trilho ANTES do fetch — a conversa anterior não
    // fica na tela enquanto a nova carrega.
    selecionarConversa(conversa) {
      this.$store.dispatch(STORE + '/setSelectedConversation', conversa)
      if (conversa.model) {
        this.$store.dispatch(STORE + '/setSelectedModel', conversa.model)
      }
      this.$store.dispatch(STORE + '/setMessagesLoading', true)
      this.$store.dispatch(STORE + '/setMessages', [])
    },
    // Falha não fica muda: tela em branco sem aviso era o sintoma.
    receberMensagensDaConversa(r) {
      if (r && r.status === HTTP_OK) {
        this.$store.dispatch(
          STORE + '/setMessages',
          (r.value && r.value.messages) || []
        )
      } else {
        // ⚠️ A frase do SERVIDOR só vai à tela quando é frase — numa queda de
        // rede `r.message` é a string técnica de `connection.js`
        // (`Network Error(500)`). Mesma régua da rota cheia
        // (`view-chat.vue::descartarConversaQueNaoAbriu`), pelo mesmo util.
        this.$showSnack({
          status: (r && r.status) || 500,
          message: mensagemAmigavelDoErro(r, ERRO_AO_ABRIR_CONVERSA),
        })
      }
      this.$store.dispatch(STORE + '/setMessagesLoading', false)
    },
    async apagarConversa(conversa) {
      if (!conversa || !conversa.id) return
      const r = await apiConversations.deleteConversation({ id: conversa.id })
      if (r && r.status === HTTP_OK) {
        // Apagar a conversa ABERTA deixaria a tela mostrando mensagens de algo
        // que não existe mais: vira conversa nova — o que também FECHA o
        // histórico, para o gestor ver o resultado da própria ação. O painel do
        // Hub divergia aqui e mantinha o histórico aberto; unificado no
        // comportamento do popup.
        const selecionada = this.$store.getters[STORE + '/selectedConversation']
        if (selecionada && selecionada.id === conversa.id) this.novaConversa()
        this.recarregarConversas()
      }
      this.$showSnack(r)
    },
    // Otimista: o nome novo entra na hora. Sem rollback, uma falha deixava na
    // tela um título que o servidor nunca gravou — e sem aviso nenhum.
    async renomearConversa({ id, topic } = {}) {
      if (!id || !topic) return
      const anterior = this.topicoAtualDaConversa(id)
      this.$store.dispatch(STORE + '/updateConversationTopic', { id, topic })
      const r = await apiConversations.renameConversation({ id, topic })
      if (r && r.status === HTTP_OK) return
      // Sem tópico anterior conhecido, restaurar '' apagaria o título em vez
      // de devolvê-lo — só há o que restaurar quando a conversa era conhecida.
      if (anterior !== null) {
        this.$store.dispatch(STORE + '/updateConversationTopic', {
          id,
          topic: anterior,
        })
      }
      this.$showSnack(r || {})
    },
    // `null` = conversa desconhecida (nem na lista, nem selecionada).
    topicoAtualDaConversa(id) {
      const lista = this.$store.getters[STORE + '/conversations'] || []
      const conversa = lista.find((c) => c.id === id)
      if (conversa) return conversa.topic
      const selecionada = this.$store.getters[STORE + '/selectedConversation']
      return selecionada && selecionada.id === id ? selecionada.topic : null
    },
  },
}
